import React from "react";
import { Editor } from "slate-react";
import PropTypes from "prop-types";
import { isKeyHotkey } from "is-hotkey";
import cn from "classnames";

const DEFAULT_NODE = "paragraph";

const isBoldHotkey = isKeyHotkey("mod+b");
const isItalicHotkey = isKeyHotkey("mod+i");
const isUnderlinedHotkey = isKeyHotkey("mod+u");
const isCodeHotkey = isKeyHotkey("mod+`");

class WYSWYG extends React.Component {
  static onKeyDown(event, change) {
    let mark;

    if (isBoldHotkey(event)) {
      mark = "bold";
    } else if (isItalicHotkey(event)) {
      mark = "italic";
    } else if (isUnderlinedHotkey(event)) {
      mark = "underlined";
    } else if (isCodeHotkey(event)) {
      mark = "code";
    } else {
      return;
    }

    event.preventDefault();
    change.toggleMark(mark);
  }

  static renderNode(props) {
    const { attributes, children, node } = props;
    switch (node.type) {
      case "block-quote":
        return <blockquote {...attributes}>{children}</blockquote>;
      case "bulleted-list":
        return <ul {...attributes}>{children}</ul>;
      case "heading-one":
        return <h1 {...attributes}>{children}</h1>;
      case "heading-two":
        return <h2 {...attributes}>{children}</h2>;
      case "list-item":
        return <li {...attributes}>{children}</li>;
      case "numbered-list":
        return <ol {...attributes}>{children}</ol>;
      default:
        return null;
    }
  }

  static renderMark(props) {
    const { children, mark, attributes } = props;
    switch (mark.type) {
      case "bold":
        return <strong {...attributes}>{children}</strong>;
      case "code":
        return <code {...attributes}>{children}</code>;
      case "italic":
        return <em {...attributes}>{children}</em>;
      case "underlined":
        return <u {...attributes}>{children}</u>;
      default:
        return null;
    }
  }

  constructor(props) {
    super(props);

    this.onClickMark = this.onClickMark.bind(this);
    this.onClickBlock = this.onClickBlock.bind(this);
    this.hasBlock = this.hasBlock.bind(this);
    this.hasMark = this.hasMark.bind(this);
    this.renderMarkButton = this.renderMarkButton.bind(this);
    this.renderBlockButton = this.renderBlockButton.bind(this);
  }

  onClickMark(event, type) {
    event.preventDefault();
    const { value } = this.props;
    const change = value.change().toggleMark(type);
    this.props.onChange(change);
  }

  onClickBlock(event, type) {
    event.preventDefault();
    const { value } = this.props;
    const change = value.change();
    const { document } = value;

    if (type !== "bulleted-list" && type !== "numbered-list") {
      const isActive = this.hasBlock(type);
      const isList = this.hasBlock("list-item");

      if (isList) {
        change
          .setBlocks(isActive ? DEFAULT_NODE : type)
          .unwrapBlock("bulleted-list")
          .unwrapBlock("numbered-list");
      } else {
        change.setBlocks(isActive ? DEFAULT_NODE : type);
      }
    } else {
      const isList = this.hasBlock("list-item");
      const isType = value.blocks.some(
        (block) =>
          !!document.getClosest(block.key, (parent) => parent.type === type)
      );

      if (isList && isType) {
        change
          .setBlocks(DEFAULT_NODE)
          .unwrapBlock("bulleted-list")
          .unwrapBlock("numbered-list");
      } else if (isList) {
        change
          .unwrapBlock(
            type === "bulleted-list" ? "numbered-list" : "bulleted-list"
          )
          .wrapBlock(type);
      } else {
        change.setBlocks("list-item").wrapBlock(type);
      }
    }

    this.props.onChange(change);
  }

  hasBlock(type) {
    const { value } = this.props;
    return value.blocks.some((node) => node.type === type);
  }

  hasMark(type) {
    const { value } = this.props;
    return value.activeMarks.some((mark) => mark.type === type);
  }

  renderMarkButton(type, icon) {
    const isActive = this.hasMark(type);
    const onMouseDown = (event) => this.onClickMark(event, type);

    return (
      <span
        className="wyswyg__button"
        onMouseDown={onMouseDown}
        data-active={isActive}
      >
        <i className={cn("fa", icon)} />
      </span>
    );
  }

  renderBlockButton(type, icon) {
    let isActive = this.hasBlock(type);

    if (["numbered-list", "bulleted-list"].includes(type)) {
      const { value } = this.props;
      const parent = value.document.getParent(value.blocks.first().key);
      isActive = this.hasBlock("list-item") && parent && parent.type === type;
    }
    const onMouseDown = (event) => this.onClickBlock(event, type);

    return (
      <span
        className="wyswyg__button"
        onMouseDown={onMouseDown}
        data-active={isActive}
      >
        <i className={cn("fas", icon)} />
      </span>
    );
  }

  renderEditor() {
    return (
      <div className="editor">
        <Editor
          placeholder="Promotion text..."
          value={this.props.value}
          onChange={this.props.onChange}
          onKeyDown={WYSWYG.onKeyDown}
          renderNode={WYSWYG.renderNode}
          renderMark={WYSWYG.renderMark}
          spellCheck
          autoFocus
        />
      </div>
    );
  }

  renderToolbar() {
    return (
      <div className="menu toolbar-menu">
        {this.renderMarkButton("bold", "fa-bold")}
        {this.renderMarkButton("italic", "fa-italic")}
        {this.renderMarkButton("underlined", "fa-underline")}
      </div>
    );
  }

  render() {
    return (
      <div id="wyswyg">
        {this.renderToolbar()}
        {this.renderEditor()}
      </div>
    );
  }
}
WYSWYG.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.shape({}).isRequired,
};

export default WYSWYG;



// WEBPACK FOOTER //
// ./client/react/Admin/components/WYSWYG/index.jsx